import { useMemo, useState } from "react";
import { CheckCircle2, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/i18n/LanguageContext";
import { LINKS } from "@/i18n/translations";
import { createLead } from "@/lib/pb";
import { Reveal } from "./Reveal";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const inputCls =
  "w-full rounded-xl border lp-hairline bg-white/[0.03] px-4 py-3 text-sm text-white placeholder:text-[#52525b] focus:border-[#f5a623]/60 focus:bg-white/[0.05] transition-colors";

const Field = ({ label, htmlFor, error, children }) => (
  <div>
    <label htmlFor={htmlFor} className="block text-sm font-semibold mb-1.5">
      {label}
    </label>
    {children}
    {error && (
      <p className="mt-1.5 text-xs text-red-400" role="alert">
        {error}
      </p>
    )}
  </div>
);

export default function LeadForm() {
  const { lang, t } = useLang();
  const f = t.form;

  // Cadena vacia, no null: los campos de texto de PocketBase rechazan null.
  const utm = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    return {
      utm_source: p.get("utm_source") || "",
      utm_medium: p.get("utm_medium") || "",
      utm_campaign: p.get("utm_campaign") || "",
      referrer: document.referrer || "",
    };
  }, []);

  const [values, setValues] = useState({
    business_name: "",
    email: "",
    whatsapp: "",
    business_type: "",
    city: "",
    terminals: "",
    consent: false,
    website: "",
  });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (key) => (e) =>
    setValues((v) => ({
      ...v,
      [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  const validate = () => {
    const errs = {};
    if (!values.business_name.trim()) errs.business_name = f.errRequired;
    if (!values.email.trim()) errs.email = f.errRequired;
    else if (!EMAIL_RE.test(values.email.trim())) errs.email = f.errEmail;
    if (!values.business_type) errs.business_type = f.errRequired;
    if (!values.terminals) errs.terminals = f.errRequired;
    if (!values.consent) errs.consent = f.errConsent;
    return errs;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (sending || done) return;
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSending(true);
    try {
      // `website` es el honeypot: se manda tal cual y lo resuelve el hook del
      // servidor, que responde exito sin guardar nada. PocketBase ignora el campo
      // porque no existe en el esquema de la coleccion.
      await createLead({
        ...values,
        language: lang,
        ...utm,
      });
      setDone(true);
    } catch (err) {
      // 429 = tope de altas por hora desde esta IP (regla `leads:create`).
      toast.error(err.status === 429 ? f.errRate : f.errServer);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="lista-espera" className="border-t lp-hairline bg-[#0c0c0f]">
      <div className="mx-auto max-w-3xl px-4 sm:px-8 py-20 sm:py-32">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#f5a623]">
            {f.kicker}
          </p>
          <h2 className="mt-3 font-black tracking-tighter text-3xl sm:text-4xl">{f.title}</h2>
          <p className="mt-4 text-[#a1a1aa] max-w-xl">{f.subtitle}</p>
        </Reveal>

        <Reveal className="mt-10">
          {done ? (
            <div
              data-testid="lead-success-message"
              className="rounded-3xl border border-green-500/30 bg-green-500/[0.06] p-10 text-center"
              role="status"
            >
              <CheckCircle2 size={48} className="mx-auto text-green-400" />
              <h3 className="mt-5 text-2xl font-extrabold tracking-tight">{f.successTitle}</h3>
              <p className="mt-3 text-[#a1a1aa]">{f.successText}</p>
              <a
                href={LINKS.demo}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="success-demo-cta"
                className="group mt-6 inline-flex items-center gap-2 rounded-full border border-[#f5a623] text-[#f5a623] font-bold px-6 py-3 hover:bg-[#f5a623] hover:text-[#09090b] transition-colors"
              >
                {f.successCta}
                <ArrowUpRight size={18} />
              </a>
            </div>
          ) : (
            <form
              data-testid="waitlist-form"
              onSubmit={submit}
              noValidate
              className="rounded-3xl border lp-hairline bg-white/[0.02] p-6 sm:p-10 grid sm:grid-cols-2 gap-5"
            >
              <div className="lp-hp" aria-hidden="true">
                <label htmlFor="lead-website">Website</label>
                <input
                  id="lead-website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={values.website}
                  onChange={set("website")}
                />
              </div>

              <Field label={f.businessName} htmlFor="lead-business-name" error={errors.business_name}>
                <input
                  id="lead-business-name"
                  data-testid="lead-business-name-input"
                  className={inputCls}
                  placeholder={f.businessNamePh}
                  value={values.business_name}
                  onChange={set("business_name")}
                  maxLength={200}
                  required
                />
              </Field>

              <Field label={f.email} htmlFor="lead-email" error={errors.email}>
                <input
                  id="lead-email"
                  data-testid="lead-email-input"
                  type="email"
                  className={inputCls}
                  placeholder={f.emailPh}
                  value={values.email}
                  onChange={set("email")}
                  required
                />
              </Field>

              <Field label={f.whatsapp} htmlFor="lead-whatsapp">
                <input
                  id="lead-whatsapp"
                  data-testid="lead-whatsapp-input"
                  type="tel"
                  className={inputCls}
                  placeholder={f.whatsappPh}
                  value={values.whatsapp}
                  onChange={set("whatsapp")}
                  maxLength={30}
                />
              </Field>

              <Field label={f.businessType} htmlFor="lead-business-type" error={errors.business_type}>
                <select
                  id="lead-business-type"
                  data-testid="lead-business-type-select"
                  className={`${inputCls} appearance-none`}
                  value={values.business_type}
                  onChange={set("business_type")}
                  required
                >
                  <option value="" disabled>
                    {f.businessTypePh}
                  </option>
                  {f.businessTypes.map((o) => (
                    <option key={o.value} value={o.value} className="bg-[#131316]">
                      {o.label}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label={f.city} htmlFor="lead-city">
                <input
                  id="lead-city"
                  data-testid="lead-city-input"
                  className={inputCls}
                  placeholder={f.cityPh}
                  value={values.city}
                  onChange={set("city")}
                  maxLength={120}
                />
              </Field>

              <Field label={f.terminals} htmlFor="lead-terminals" error={errors.terminals}>
                <select
                  id="lead-terminals"
                  data-testid="lead-terminals-select"
                  className={`${inputCls} appearance-none`}
                  value={values.terminals}
                  onChange={set("terminals")}
                  required
                >
                  <option value="" disabled>
                    {f.terminalsPh}
                  </option>
                  {f.terminalOptions.map((o) => (
                    <option key={o.value} value={o.value} className="bg-[#131316]">
                      {o.label}
                    </option>
                  ))}
                </select>
              </Field>

              <div className="sm:col-span-2">
                <label className="flex items-start gap-3 cursor-pointer text-sm text-[#a1a1aa]">
                  <input
                    type="checkbox"
                    data-testid="lead-consent-checkbox"
                    checked={values.consent}
                    onChange={set("consent")}
                    className="mt-0.5 w-4 h-4 accent-[#f5a623]"
                    required
                  />
                  {f.consent}
                </label>
                {errors.consent && (
                  <p className="mt-1.5 text-xs text-red-400" role="alert">
                    {errors.consent}
                  </p>
                )}
              </div>

              <div className="sm:col-span-2">
                <button
                  type="submit"
                  data-testid="lead-submit-button"
                  disabled={sending}
                  className="w-full sm:w-auto inline-flex justify-center rounded-full bg-[#f5a623] text-[#09090b] font-bold px-8 py-3.5 hover:bg-[#ffbb3d] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {sending ? f.submitting : f.submit}
                </button>
              </div>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}
